import { IFilteredBillRequest } from "../redux/actions/pintagAction";
import { CustomerSearchMode } from "../typings/enum/CustomerSearchMode";
import {
  BillFilterDateType,
  BillPaymentStatus,
  BillPickupStatus,
} from "../typings/enum/paymentTypes";

export const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// Auth endpoints
export const LOGIN_URL = `${BASE_URL}/auth/sign-in`;
export const SIGNUP_URL = `${BASE_URL}/auth/signup`;
export const GET_ALL_USERS = `${BASE_URL}/user/users`;
export const GET_ALL_ROLES = `${BASE_URL}/role`;
export const UPDATE_USER = (id: number) => `${BASE_URL}/user/update/${id}`;
export const UPDATE_ROLE_NAME = (id: number) => `${BASE_URL}/role/${id}/update`;
export const UPDATE_PERMISSIONS = (id: number) =>
  `${BASE_URL}/user/role/${id}/permissions`;
export const SET_USER_ROLE = `${BASE_URL}/user/set-role`;
export const SEARCH_CUSTOMER = (query: string, status: string) =>
  `${BASE_URL}/customer/search?query=${query}&status=${status}`;
export const CREATE_CUSTOMER = `${BASE_URL}/customer/create`;
export const UPDATE_CUSTOMER = `${BASE_URL}/customer/update`;
export const CHANGE_PASSWORD = `${BASE_URL}/auth/password/change`;
export const LOGIN_USERS = `${BASE_URL}/user/get-login-users`;

// Pricing endpoints
export const GET_ALL_CATEGORIES = `${BASE_URL}/category`;
export const REORDER_CATEGORIES = `${BASE_URL}/category/reorder`;
export const GET_ALL_SECOND_SUB_CATEGORIES = `${BASE_URL}/second-subcategory`;
export const GET_ALL_SUB_CATEGORIES = (id: number) =>
  `${BASE_URL}/subcategory?category=${id}`;
export const REORDER_SUB_CATEGORIES = `${BASE_URL}/subcategory/reorder`;
export const CREATE_SUBCATEGORY_WITH_IMAGE = `${BASE_URL}/subcategory/create`;
export const CREATE_MATERIAL = `${BASE_URL}/material`;
export const REORDER_MATERIALS = `${BASE_URL}/material/reorder`;
// Pieces checked in per garment / fabric / style for a service (Price Setup "Most used")
export const GET_MENU_USAGE = (categoryId: number, days = 90) =>
  `${BASE_URL}/menu-order/usage?categoryId=${categoryId}&days=${days}`;
export const UPDATE_MATERIAL = (id: number) => `${BASE_URL}/material/${id}`;
export const UPDATE_CATEGORY = `${BASE_URL}/category`;
export const PATCH_CATEGORY = (id: number) => `${BASE_URL}/category/${id}`;
export const DELETE_CATEGORY = (id: number) => `${BASE_URL}/category/${id}`;
export const UPDATE_SUBCATEGORY = `${BASE_URL}/subcategory`;
export const DELETE_SUBCATEGORY = (id: number) => `${BASE_URL}/subcategory/${id}`;
export const DELETE_MATERIAL = (id: number) => `${BASE_URL}/material/${id}`;
export const DELETE_SECOND_SUBCATEGORY = (id: number) => `${BASE_URL}/second-subcategory/${id}`;
export const CREATE_SECOND_SUBCATEGORY = `${BASE_URL}/second-subcategory/create-with-relationship`;
export const REORDER_SECOND_SUBCATEGORIES = `${BASE_URL}/second-subcategory/reorder`;
export const UPDATE_SECOND_SUBCATEGORY = `${BASE_URL}/second-subcategory`;
export const UPSERT_PRICE = `${BASE_URL}/price/upsert`;
// Price-list scoping: with neither param the backend uses the default list;
// with customerId, the customer's list (falling back to the default).
export interface PriceListScope {
  priceListId?: number | null;
  customerId?: number | string | null;
}
const priceListScopeQuery = ({ priceListId, customerId }: PriceListScope = {}) => {
  const params = new URLSearchParams();
  if (priceListId != null) params.set("priceListId", String(priceListId));
  else if (customerId != null) params.set("customerId", String(customerId));
  const query = params.toString();
  return query ? `?${query}` : "";
};
export const GET_ALL_PRICINGS = (scope?: PriceListScope) =>
  `${BASE_URL}/price${priceListScopeQuery(scope)}`;
export const GET_UNAVAILABLE_MENU_ITEMS = (scope?: PriceListScope) =>
  `${BASE_URL}/price/unavailable${priceListScopeQuery(scope)}`;
export const DELETE_PRICE_ITEM = `${BASE_URL}/price/item`;
export const UPLOAD_PRICE_LIST = (priceListId?: number | null) =>
  `${BASE_URL}/price/upload-prices${priceListScopeQuery({ priceListId })}`;
export const UPLOAD_ALTERATION_PRICE_LIST = (priceListId?: number | null) =>
  `${BASE_URL}/price/upload-prices-alteration${priceListScopeQuery({ priceListId })}`;
export const PRICE_LISTS = `${BASE_URL}/price-list`;
export const PRICE_LIST_BY_ID = (id: number) => `${BASE_URL}/price-list/${id}`;
export const CLONE_PRICE_LIST = (id: number, fromId: number, overwrite: boolean) =>
  `${BASE_URL}/price-list/${id}/clone?from=${fromId}&overwrite=${overwrite}`;
export const SET_DEFAULT_PRICE_LIST = (id: number) =>
  `${BASE_URL}/price-list/${id}/set-default`;
export const UPDATE_CUSTOMER_PRICE_LIST = (customerId: number | string) =>
  `${BASE_URL}/customer/${customerId}/price-list`;
export const CREATE_STATION = `${BASE_URL}/station/create`;

export const GET_CATEGORY_RELATIONSHIP = `${BASE_URL}/price/relationship/category`;
export const GET_CATEGORY_MATERIAL_RELATIONSHIP = `${BASE_URL}/price/relationship/category-material`;
export const GET_SUB_CATEGORY_MATERIAL_RELATIONSHIP = `${BASE_URL}/price/relationship/sub-category-material`;

export const GET_ALTERATION_SUB_CATEGORIES = `${BASE_URL}/alteration-sub-category`;
export const GET_ALL_ALTERATION_PRICE = `${BASE_URL}/alteration`;

export const GET_FABRIC_TYPES = (id: number) =>
  `${BASE_URL}/fabrics?category=${id}`;

// StoreCredit endpoints
export const ADD_STORE_CREDIT = `${BASE_URL}/store-credit/add`;
export const GET_STORE_CREDIT_HISTORY = (id: number) =>
  `${BASE_URL}/store-credit/customers/${id}/history`;
export const DELETE_STORE_CREDIT = (customerId: number, noteId: number) =>
  `${BASE_URL}/store-credit/customer/${customerId}/credit/${noteId}`;

// Customer Endpoints
export const CUSTOMER_CONFIG = `${BASE_URL}/customer-config`;
export const GET_CUSTOMER_BY_ID = (id: string) => `${BASE_URL}/customer/${id}`;
// export const GET_CUSTOMER_BILLS_BY_ID = (id: string) =>
//   `${BASE_URL}/bill/customer/${id}`; // old one
export const GET_CUSTOMER_BILLS_BY_ID = (
  customerId: number,
  size: number,
  page: number,
  paymentFilterStatus?: BillPaymentStatus,
  filterDateType?: BillFilterDateType,
  startDate?: string,
  endDate?: string,
  pickingFilterStatus?: BillPickupStatus,
  invoiceNumber?: string
) =>
  `${BASE_URL}/bill/customer/${customerId}?size=${size}&page=${page}${invoiceNumber ? `&invoiceNumber=${encodeURIComponent(invoiceNumber)}` : ""
  }${paymentFilterStatus ? `&paymentFilterStatus=${paymentFilterStatus}` : ""}${filterDateType ? `&filterDateType=${filterDateType}` : ""
  }${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""
  }${pickingFilterStatus ? `&pickingFilterStatus=${pickingFilterStatus}` : ""}`;

export const GET_CUSTOMER_CART_INFO_BY_ID = (id: string) =>
  `${BASE_URL}/customer/${id}/cart-info`;

export const NEW_BILL = `${BASE_URL}/bill/new-bill`;
export const GET_BILL_BY_ID = (billId: string) => `${BASE_URL}/bill/${billId}`;
// Ticket barcodes encode the formatted invoice number (e.g. "DC26-2421",
// bill.billId), not the internal numeric PK GET_BILL_BY_ID expects - so a
// scanned ticket has to be resolved through the filter endpoint first.
export const GET_BILL_BY_INVOICE_NUMBER = (invoiceNumber: string) =>
  `${BASE_URL}/bill/filter?invoiceNumber=${encodeURIComponent(invoiceNumber)}&size=1`;
export const EDIT_BILL = (billId: number) => `${BASE_URL}/bill/${billId}`;
export const UPDATE_BILL_PINTAG = (billId: number) =>
  `${BASE_URL}/bill/${billId}/pintag`;
export const UPDATE_BILL_DISCOUNT = (billId: number) =>
  `${BASE_URL}/bill/${billId}/discount`;

export const GET_ASSIGNED_CONVEYORS = (
  conveyorId?: number,
  slotId?: number,
  // Cashiers search by the bill number they scanned ("PV25-1234"), not by the
  // internal PK the rows are keyed on — the API resolves it server-side.
  billNumber?: string,
  date?: string,
  page: number = 0,
  size: number = 10
) =>
  `${BASE_URL}/assigned-conveyors?size=${size}&page=${page}` +
  `${typeof conveyorId === "number" && !isNaN(conveyorId)
    ? `&conveyorId=${conveyorId}`
    : ""
  }` +
  `${typeof slotId === "number" && !isNaN(slotId) ? `&slotId=${slotId}` : ""}` +
  `${billNumber && billNumber.trim()
    ? `&billNumber=${encodeURIComponent(billNumber.trim())}`
    : ""
  }` +
  // The API filters on a startDate/endDate range; a bare `date` param was
  // dropped by @ModelAttribute, so the date picker had no effect at all.
  `${date ? `&startDate=${date}&endDate=${date}` : ""}`;

export const GET_ASSIGNED_VALIDATION = (slotKey: string, billId: string) => {
  if (slotKey === undefined || billId === undefined) {
    throw new Error(
      "GET_ASSIGNED_VALIDATION: slotKey and billId are required."
    );
  }

  const slot = encodeURIComponent(String(slotKey).trim());
  const bill = encodeURIComponent(String(billId).trim());

  return `${BASE_URL}/assigned-conveyors/is-assigned/conveyor-slot/${slot}/bill/${bill}`;
};

export const ASSIGN_RACKS = `${BASE_URL}/assigned-conveyors/assign`;

export const ADD_BOX_STORAGE = `${BASE_URL}/box-storage-config`;
export const GET_BOX_STORAGE = `${BASE_URL}/box-storage-config/bill`;
export const REMOVE_BOX_STORAGE = `${BASE_URL}/box-storage-config`;
export const GET_AVAILABLE_BOXES = `${BASE_URL}/box-storage-config/boxes-with-remaining-slots`;
export const VALIDATE_BOX_STORAGE = `${BASE_URL}/box-storage/validate`;
export const CREATE_BOX = `${BASE_URL}/box-storage-config/new-box`;
export const GET_BOX_STORAGE_HISTORY = `${BASE_URL}/box-storage-config/customer`;
export const RENEW_BOX_STORAGE = `${BASE_URL}/box-storage-config/renew`;

// Rack Endpoints
export const GET_ALL_RACKS = `${BASE_URL}/conveyor-input`;
export const ADD_RACK = GET_ALL_RACKS;
export const REGISTER_RACK_BILL = `${BASE_URL}/rack-bill-register`;
export const GET_ALL_RACK_BILL = `${BASE_URL}/rack-bill-register/get-all`;
export const UPDATE_RACK = `${BASE_URL}/conveyor-input`;
// Racks are conveyor inputs — deleting via the legacy /rack resource removed a
// row from an unrelated table and left the conveyor in place.
export const DELETE_RACK = (id: number) => `${BASE_URL}/conveyor-input/${id}`;

// User Notes Endpoints
export const CREATE_AND_UPDATE_USER_NOTE = `${BASE_URL}/user-note`;
export const GET_USER_NOTES_BY_ID = (id: string) =>
  `${BASE_URL}/user-note/customer/${id}`;
export const DELETE_USER_NOTE = (id: string) => `${BASE_URL}/user-note/${id}`;

// Bill Notes Endpoints
export const CREATE_AND_UPDATE_BILL_NOTE = `${BASE_URL}/bill-note`;
export const GET_BILL_NOTES_BY_ID = (id: number) =>
  `${BASE_URL}/bill-note/bill/${id}`;
export const DELETE_BILL_NOTE = (id: string) => `${BASE_URL}/bill-note/${id}`;

// Discount endpoints
export const GET_ALL_STORE_DISCOUNTS = (id: number) =>
  `${BASE_URL}/discount/store/${id}`;
export const CREATE_DISCOUNT = `${BASE_URL}/discount`;
export const DELETE_DISCOUNT = (id: string) => `${BASE_URL}/discount/${id}`;
export const UPDATE_DISCOUNT = `${BASE_URL}/discount`;

// Merchant endpoints
export const GET_MERCHANT_DETAILS = (whiteLabel: string) =>
  `${BASE_URL}/merchant/${whiteLabel}`;
export const GET_MERCHANT_STORES = (whiteLabel: string) =>
  `${BASE_URL}/merchant/${whiteLabel}/stores`;
export const GET_ALL_MERCHANTS = `${BASE_URL}/merchant/all`;
export const UPDATE_MERCHANT_TAX_CONFIG = (merchantId: number) =>
  `${BASE_URL}/merchant/${merchantId}/tax-config`;
export const UPDATE_MERCHANT_BILL_ITEM_LIMIT = (merchantId: number) =>
  `${BASE_URL}/merchant/${merchantId}/bill-item-limit`;

// Tax Exemption endpoints
export const GET_TAX_EXEMPTION_BY_CUSTOMER = (customerId: number) =>
  `${BASE_URL}/tax-exemption/customer/${customerId}`;
export const ADD_TAX_EXEMPTION = (customerId: number) =>
  `${BASE_URL}/tax-exemption/customer/${customerId}`;
export const UPDATE_TAX_EXEMPTION = (customerId: number) =>
  `${BASE_URL}/tax-exemption/customer/${customerId}`;
export const DELETE_TAX_EXEMPTION = (customerId: number) =>
  `${BASE_URL}/tax-exemption/customer/${customerId}`;

// Payment endpoints
export const ADD_PAYMENT = `${BASE_URL}/payment/create`;

// store endpoints
export const ADD_STORE_CONFIG = `${BASE_URL}/store-config`;
export const GET_STORE_CONFIG = (storeId: number, merchantId: number) =>
  `${BASE_URL}/store-config/merchant/${merchantId}/store/${storeId}`;

// store setup (Admin → Configs → Store Setup)
export const STORE_SETUP = (storeId: number, merchantId: number) =>
  `${BASE_URL}/store-setup/merchant/${merchantId}/store/${storeId}`;
export const STORE_SETUP_TAXES = (storeId: number, merchantId: number) =>
  `${BASE_URL}/store-setup/merchant/${merchantId}/store/${storeId}/taxes`;
export const STORE_SETUP_TAX = (id: number) => `${BASE_URL}/store-setup/taxes/${id}`;

// store closure date endpoints
export const GET_STORE_CLOSURE_DATES = (storeId: number, merchantId: number) =>
  `${BASE_URL}/store-closure-date/merchant/${merchantId}/store/${storeId}`;
export const ADD_STORE_CLOSURE_DATE = `${BASE_URL}/store-closure-date`;
export const UPDATE_STORE_CLOSURE_DATE = (id: number) =>
  `${BASE_URL}/store-closure-date/${id}`;
export const DELETE_STORE_CLOSURE_DATE = (id: number) =>
  `${BASE_URL}/store-closure-date/${id}`;

// Pintag endpoints
export const GET_ALL_COLOR_PINTAGS = `${BASE_URL}/pintag-color`;
export const REORDER_PINTAGS = `${BASE_URL}/pintag-color/reorder`;
export const CREATE_COLOR_PINTAG = GET_ALL_COLOR_PINTAGS;
export const UPDATE_COLOR_PINTAG = GET_ALL_COLOR_PINTAGS;
export const DELETE_COLOR_PINTAG = (id: number) =>
  `${BASE_URL}/pintag-color/${id}`;
// Pin tag boxes (000–999 per colour; reported issues 3.p)
export const GET_PINTAG_BOXES = `${BASE_URL}/pintag-box`;
export const OPEN_PINTAG_BOX = `${BASE_URL}/pintag-box/open`;
export const GET_PINTAG_BOOKS = (colorId: number) => `${BASE_URL}/pintag-box/${colorId}/books`;
export const RELEASE_PINTAG_BOOK = (bookId: number) => `${BASE_URL}/pintag-box/books/${bookId}/release`;
export const UPDATE_COLOR_PINTAG_ORDER = `${BASE_URL}/pintag-color/reorder`;

export const GET_ALL_SERVICE_PINTAGS = `${BASE_URL}/service-pintag`;
export const CREATE_SERVICE_PINTAG = GET_ALL_SERVICE_PINTAGS;
export const UPDATE_SERVICE_PINTAG = GET_ALL_SERVICE_PINTAGS;
export const DELETE_SERVICE_PINTAG = (id: number) =>
  `${BASE_URL}/service-pintag/${id}`;

// damage endpoints
export const GET_ALL_DAMAGES = `${BASE_URL}/damage/merchant/1/store/1`;

//instructions endpoints
export const GET_ALL_INSTRUCTIONS = `${BASE_URL}/instruction/merchant/1/store/1`;

//spots endpoints
export const GET_ALL_SPOTS = `${BASE_URL}/spot/store/1/merchant/1`;

//upcharges endpoints
export const GET_ALL_UPCHARGES = `${BASE_URL}/upcharge/store/1/merchant/1`;

//customer insights endpoints
export const GET_CUSTOMER_INSIGHTS = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/overview`;

//customer latest bill details endpoints
export const GET_CUSTOMER_LATEST_BILL_DETAILS = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/latest-bill`;

//customer average spendings endpoints
export const GET_CUSTOMER_AVG_SPENDINGS = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/spending-analytics`;

export const GET_CUSTOMER_INTERACTIONS_BY_DAY = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/average-activity`;

export const GET_CUSTOMER_OUTSTANDING = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/outstanding-amount`;

//customer current year service usage chart endpoints
export const GET_CUSTOMER_CURRENT_YEAR_SERVICE_USAGE = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/service-usage/current-year`;

//customer all time service usage chart endpoints
export const GET_CUSTOMER_ALL_TIME_SERVICE_USAGE = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/service-usage/all-time`;

//customer report history endpoint
export const GET_CUSTOMER_REPORT_HISTORY = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/report-history`;

//customer generated reports endpoints
export const GENERATE_CUSTOMER_REPORT = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/reports/generate`;
export const GET_CUSTOMER_GENERATED_REPORTS = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/reports`;
export const GET_CUSTOMER_MONTHLY_INVOICES = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/monthly-invoices`;
export const GET_IS_MONTHLY_CUSTOMER = (id: number) =>
  `${BASE_URL}/insights/customer/${id}/is-monthly-customer`;
export const GET_CUSTOMER_REPORT_DATA = (id: number, reportType: string, periodStart?: string, periodEnd?: string, billIds?: number[]) => {
  const params = new URLSearchParams({ reportType });
  if (periodStart) params.append("periodStart", periodStart);
  if (periodEnd) params.append("periodEnd", periodEnd);
  if (billIds && billIds.length > 0) billIds.forEach((bid) => params.append("billIds", String(bid)));
  return `${BASE_URL}/insights/customer/${id}/report-data?${params.toString()}`;
};

//customer refund statistics endpoints
export const GET_CUSTOMER_REFUND_STATS = (id: number) =>
  `${BASE_URL}/refund/customer/${id}/total`;
export const GET_CUSTOMER_REFUND_STATS_BY_YEAR = (id: number, year: number) =>
  `${BASE_URL}/refund/customer/${id}/year/${year}/total`;
export const GET_CUSTOMER_REFUND_HISTORY = (id: number) =>
  `${BASE_URL}/refund/customer/${id}/`;

//admin customization endpoints
export const ADD_UPCHARGE = (merchantId: number, storeId: number) =>
  `${BASE_URL}/upcharge/store/${storeId}/merchant/${merchantId}`;

export const UPDATE_UPCHARGE = (merchantId: number, storeId: number) =>
  `${BASE_URL}/upcharge/store/${storeId}/merchant/${merchantId}`;

export const DELETE_UPCHARGE = (
  merchantId: number,
  storeId: number,
  upChargeId: number
) =>
  `${BASE_URL}/upcharge/${upChargeId}/store/${storeId}/merchant/${merchantId}`;

export const DELETE_UPCHARGE_VARIANT = (
  merchantId: number,
  storeId: number,
  upChargeId: number,
  variantId: number
) =>
  `${BASE_URL}/upcharge/store/${storeId}/merchant/${merchantId}/upcharge/${upChargeId}/variant/${variantId}`;

export const GET_ALL_PRICE_OVERRIDE_CONFIGS = `${BASE_URL}/price-override-config`;
export const GET_ACTIVE_PRICE_OVERRIDE_CONFIGS = `${BASE_URL}/price-override-config/active`;
export const CREATE_PRICE_OVERRIDE_CONFIG = `${BASE_URL}/price-override-config`;
export const UPDATE_PRICE_OVERRIDE_CONFIG = `${BASE_URL}/price-override-config`;
export const DELETE_PRICE_OVERRIDE_CONFIG = (id: number) => `${BASE_URL}/price-override-config/${id}`;
export const SPECIAL_CHARGE_CONFIG = `${BASE_URL}/special-charge-config`;

//admin customization endpoints
export const ADD_SPOT = (merchantId: number, storeId: number) =>
  `${BASE_URL}/spot/store/${storeId}/merchant/${merchantId}`;

export const UPDATE_SPOTS = (merchantId: number, storeId: number) =>
  `${BASE_URL}/spot/store/${storeId}/merchant/${merchantId}`;

export const DELETE_SPOT = (
  spotId: number,
  storeId: number,
  merchantId: number
) => `${BASE_URL}/spot/${spotId}/store/${storeId}/merchant/${merchantId}`;

//admin customization endpoints
export const ADD_INSTRUCTIONS = (merchantId: number, storeId: number) =>
  `${BASE_URL}/instruction/merchant/${merchantId}/store/${storeId}`;

export const DELETE_INSTRUCTION = (
  merchantId: number,
  storeId: number,
  instructionId: number
) => `${BASE_URL}/instruction/${storeId}/${merchantId}/${instructionId}`;

export const UPDATE_INSTRUCTION = (merchantId: number, storeId: number) =>
  `${BASE_URL}/instruction/merchant/${merchantId}/store/${storeId}`;

//redo reasons endpoints
export const GET_ALL_REDO_REASONS = `${BASE_URL}/redo-reason/merchant/1/store/1`;

//cash drawer reasons endpoints
export const GET_ALL_CASH_DRAWER_REASONS = `${BASE_URL}/cash-drawer-reason/merchant/1/store/1`;

export const ADD_CASH_DRAWER_REASON = (merchantId: number, storeId: number) =>
  `${BASE_URL}/cash-drawer-reason/merchant/${merchantId}/store/${storeId}`;

export const DELETE_CASH_DRAWER_REASON = (
  merchantId: number,
  storeId: number,
  cashDrawerReasonId: number
) => `${BASE_URL}/cash-drawer-reason/${storeId}/${merchantId}/${cashDrawerReasonId}`;

export const UPDATE_CASH_DRAWER_REASON = (merchantId: number, storeId: number) =>
  `${BASE_URL}/cash-drawer-reason/merchant/${merchantId}/store/${storeId}`;

export const ADD_REDO_REASON = (merchantId: number, storeId: number) =>
  `${BASE_URL}/redo-reason/merchant/${merchantId}/store/${storeId}`;

export const DELETE_REDO_REASON = (
  merchantId: number,
  storeId: number,
  redoReasonId: number
) => `${BASE_URL}/redo-reason/${storeId}/${merchantId}/${redoReasonId}`;

export const UPDATE_REDO_REASON = (merchantId: number, storeId: number) =>
  `${BASE_URL}/redo-reason/merchant/${merchantId}/store/${storeId}`;

//cashier onbooarding endpoints
// export const CASHIER_ONBOARDING = `${BASE_URL}/station/request-new`;
// export const OPEN_CASHIER = `${BASE_URL}/testing-123/open`;
// // export const OPEN_CASHIER = `${BASE_URL}/cashier-point/station/open`;
// export const GET_STATION_ACCESS = (cashierId: number) =>
//   `${BASE_URL}/station/access-status/${cashierId}`;
// export const GET_CASHIER_STATION_LIST = `${BASE_URL}/station`;
// export const GRANT_STATION_ACCESS = (stationId: number) =>
//   `${BASE_URL}/station/${stationId}/grant-access`;
export const NEW_STATION = `${BASE_URL}/station/request/access/new`;
export const GET_ALL_STATIONS = `${BASE_URL}/station/requests/all`;

export const GET_ALL_CASHIER_POINTS = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/cashier-points/available`;

// Add new endpoints for enhanced station management
export const GET_ALL_STATIONS_WITH_STATUS = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/all`;

export const GET_AVAILABLE_STATIONS_FOR_ONBOARDING = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/available-for-onboarding`;

export const GET_GRANTED_STATIONS = `${BASE_URL}/station/access-granted`;
export const GET_DENIED_STATIONS = `${BASE_URL}/station/access-denied`;
export const GET_STATION_BY_ID = (id: number) =>
  `${BASE_URL}/station/${id}/info`;
export const UPDATE_STATION_ACCESS = (id: number) =>
  `${BASE_URL}/station/request/access/${id}/grant`;

export const DENY_STATION_ACCESS = (id: number) =>
  `${BASE_URL}/station/request/access/${id}/deny`;

export const DEACTIVATE_STATION = (id: number) =>
  `${BASE_URL}/station/${id}/deactivate`;

export const ACTIVATE_STATION = (id: number) =>
  `${BASE_URL}/station/${id}/activate`;

export const OPEN_STATION_CASHIER = `${BASE_URL}/station/open`;

export const GET_STATION_CASHIER_SUMMARY = (id: number) =>
  `${BASE_URL}/station/${id}/day-end/station-summary`;

export const GET_ALL_STATIONS_DAY_END_SUMMARY = (
  storeId: number,
  date?: string
) =>
  `${BASE_URL}/station/store/${storeId}/day-end/station-summary${date ? `?date=${date}` : ""
  }`;

export const GET_STATION_SUMMARY = (id: number) =>
  `${BASE_URL}/station/${id}/summary`;

export const GET_ALL_STATION_SUMMARY = (id: number) =>
  `${BASE_URL}/station/store/${id}/summary`;

export const ADD_CASH = `${BASE_URL}/station/add-cash`;
export const REMOVE_CASH = `${BASE_URL}/station/remove-cash`;

export const GET_STORE_SUMMARY = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/day-end/store-summary`;

export const CLOSE_STATION_CASHIER = `${BASE_URL}/station/close`;
export const END_DAY = `${BASE_URL}/station/new/day-end/station-summary`;

export const LATEST_CASHIER_SESSION = (stationId: number) =>
  `${BASE_URL}/station/${stationId}/latest-session/summary`;

//admin customization endpoints
export const ADD_DAMAGE = (merchantId: number, storeId: number) =>
  `${BASE_URL}/damage/merchant/${merchantId}/store/${storeId}`;

export const DELETE_DAMAGE = (
  merchantId: number,
  storeId: number,
  damageId: number
) => `${BASE_URL}/damage/${damageId}/merchant/${merchantId}/store/${storeId}`;

export const UPDATE_DAMAGE = (merchantId: number, storeId: number) =>
  `${BASE_URL}/damage/merchant/${merchantId}/store/${storeId}`;

export const PICKUP_ITEM = `${BASE_URL}/pickup-items`;
export const GET_BILL_ITEM_IDS = (billId: number) =>
  `${BASE_URL}/bill/${billId}/items`;

export const INVOICE_HISTORY = (billId: number) =>
  `${BASE_URL}/invoice-history/${billId}`;

export const VOID_INVOICE = (billId: number) =>
  `${BASE_URL}/bill/${billId}/void`;
export const BILL_PAYMENT_HISTORY = `${BASE_URL}/payment/filter`;

export const REFUND_INVOICE = `${BASE_URL}/refund/customer`;
export const VALIDATE_PINTAGS = `${BASE_URL}/pintag-color/tags/validate`;

export const PINTAG_SEARCH = (params: IFilteredBillRequest) => {
  const searchParams = new URLSearchParams();

  // Add the existing query parameters
  if (params.customerId)
    searchParams.append("customerId", String(params.customerId));
  if (params.pintagColor)
    searchParams.append("pintagColor", params.pintagColor);
  if (params.pintagTag && params.pintagTag.length)
    searchParams.append("pintagTags", params.pintagTag.join(","));
  if (params.invoice && params.invoice.length)
    searchParams.append("billId", params.invoice.join(","));
  if (params.generalQuery)
    searchParams.append("generalQuery", params.generalQuery);
  if (params.dueDateFrom)
    searchParams.append("dueDateFrom", params.dueDateFrom);
  if (params.rackLocation)
    searchParams.append("rackLocation", params.rackLocation);

  if (params.page !== undefined)
    searchParams.append("page", String(params.page));
  if (params.size !== undefined)
    searchParams.append("size", String(params.size));

  return `${BASE_URL}/bill/filter?${searchParams.toString()}`;
};

export const GET_RACK = `${BASE_URL}/rack`;

export const GET_PRICE_LIST = (id: string) =>
  `${BASE_URL}/price/range/category/${id}`;

export const GET_REFUNDS_FOR_STATION = (stationId: number) =>
  `${BASE_URL}/station/${stationId}/refunds`;

export const GET_REFUNDS_FOR_STORE = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/refunds`;

export const GET_STORE_CATEGORY = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/category/summary`;

export const GET_CASHIER_INFO = (deviceId: string) =>
  `${BASE_URL}/station/device/${deviceId}/info`;

export const STORE_STATUS_CHECK = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/status-check`;

export const SAVE_DRAFT = `${BASE_URL}/draft-bill-session`;

export const GET_CASHIER_DRAFTS = (cashierId: number) =>
  `${BASE_URL}/draft-bill-session/cashier/${cashierId}`;

export const DELETE_CASHIER_DRAFTS = (billId: number) =>
  `${BASE_URL}/draft-bill-session/${billId}`;

export const GET_STATION_ACCESS_SESSIONS = (storeId: number) =>
  `${BASE_URL}/station/store/${storeId}/access-sessions`;

export const ADMIN_CLOSE_STATION = `${BASE_URL}/station/admin/close`;

// Manager access log endpoints
export const VERIFY_AND_LOG_MANAGER_ACCESS = `${BASE_URL}/manager-access-log/verify-and-log`;
export const GET_MANAGER_ACCESS_LOGS = `${BASE_URL}/manager-access-log`;

// Manager avatar grid endpoints
export const GET_MANAGERS_WITH_ACCESS_CODE = `${BASE_URL}/user/managers`;
export const SET_USER_ACCESS_CODE = (id: number) => `${BASE_URL}/user/${id}/access-code`;

// NFC card endpoints
export const SET_USER_NFC_CARD = (id: number) => `${BASE_URL}/user/${id}/nfc-card`;
export const ASSIGN_CUSTOMER_NFC_TAG = (customerId: number) => `${BASE_URL}/customer/${customerId}/nfc-tag`;
export const GET_CUSTOMER_BY_NFC_TAG = (nfcTagId: string) => `${BASE_URL}/customer/nfc-tag/${nfcTagId}`;
export const GET_CUSTOMER_NFC_TAGS = (customerId: number) => `${BASE_URL}/customer/${customerId}/nfc-tags`;
export const ADD_CUSTOMER_NFC_TAG = (customerId: number) => `${BASE_URL}/customer/${customerId}/nfc-tags`;
export const DELETE_CUSTOMER_NFC_TAG = (customerId: number, nfcTagId: string) => `${BASE_URL}/customer/${customerId}/nfc-tags/${nfcTagId}`;
export const VERIFY_AND_LOG_NFC_ACCESS = `${BASE_URL}/manager-access-log/verify-and-log-nfc`;
export const VERIFY_NFC_UNLOCK = `${BASE_URL}/auth/verify-nfc-unlock`;

// Permission management endpoints
export const GET_ALL_PERMISSIONS = `${BASE_URL}/permission`;
export const CREATE_PERMISSION = `${BASE_URL}/permission`;
export const DELETE_PERMISSION = (id: number) => `${BASE_URL}/permission/${id}`;

// Account lock endpoints
export const GET_USER_BY_ID = (id: number) => `${BASE_URL}/user/${id}`;
export const LOCK_USER_ACCOUNT = (id: number) => `${BASE_URL}/user/${id}/lock`;
export const UNLOCK_USER_ACCOUNT = (id: number) => `${BASE_URL}/user/${id}/unlock`;
export const RESET_UNLOCK_ATTEMPTS = (id: number) => `${BASE_URL}/user/${id}/reset-unlock-attempts`;

// Session management endpoints
export const VALIDATE_SESSION = `${BASE_URL}/auth/validate`;
export const REVOKE_USER_SESSION = (id: number) => `${BASE_URL}/user/${id}/session`;

// Customer release signature endpoints
export const SAVE_CUSTOMER_RELEASE_SIGNATURE = (customerId: number) =>
  `${BASE_URL}/customer-release-signature/customer/${customerId}`;
export const GET_CUSTOMER_RELEASE_SIGNATURES = (customerId: number) =>
  `${BASE_URL}/customer-release-signature/customer/${customerId}`;
export const GET_CUSTOMER_LATEST_RELEASE_SIGNATURE = (customerId: number) =>
  `${BASE_URL}/customer-release-signature/customer/${customerId}/latest`;
export const GET_ALL_RELEASES = `${BASE_URL}/customer-release-signature/release`;
export const CREATE_RELEASE = `${BASE_URL}/customer-release-signature/release`;
export const UPDATE_RELEASE = (id: number) =>
  `${BASE_URL}/customer-release-signature/release/${id}`;
export const GET_RELEASE_BY_NAME = (name: string) =>
  `${BASE_URL}/customer-release-signature/release/by-name/${encodeURIComponent(name)}`;
